'use client';
import React from 'react';

type Testimonial = {
  name: string;
  role: string;
  product: string;
  text: string;
  stars: number;
};

const testimonials: Testimonial[] = [
  // Intentionally cleared per request.
];

export default function TestimonialsSection() {
  const [reviewsList, setReviewsList] = React.useState<Testimonial[]>(testimonials);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch('/api/google-reviews');
        if (res.ok) {
          const data = await res.json();
          if (data.reviews && data.reviews.length > 0) {
            setReviewsList(data.reviews);
          }
        }
      } catch (error) {
        console.error('Failed to fetch reviews', error);
      } finally {
        setLoading(false);
      }
    }
    fetchReviews();
  }, []);

  return (
    <section className="py-12">
      <div className="max-w-[1500px] mx-auto px-4">
        <div className="rounded-none border border-slate-200 bg-white px-4 py-8 shadow-sm sm:px-5">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-[11px] font-bold tracking-[0.2em] text-[#eab308] uppercase mb-2">
              PHẢN HỒI
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">
              Khách Hàng Nói Gì Về <span className="text-[#eab308]">DN TECH</span>?
            </h2>
            <p className="text-sm text-gray-500">
              Hàng nghìn khách hàng đã tin tưởng lựa chọn chúng tôi
            </p>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[1, 2, 3].map((_, idx) => (
                <div
                  key={idx}
                  className="bg-slate-50 rounded-none h-64 border border-slate-100"
                ></div>
              ))}
            </div>
          ) : reviewsList.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviewsList.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-none p-6 border border-gray-100 hover:-translate-y-0.5 transition-transform duration-200 flex flex-col h-full relative group"
                >
                  {/* Top part: Stars & Quote Icon */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-1">
                      {[...Array(item.stars)].map((_, i) => (
                        <svg
                          key={i}
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          className="text-[#eab308]"
                          viewBox="0 0 16 16"
                        >
                          <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z" />
                        </svg>
                      ))}
                    </div>
                    {/* Quote SVG */}
                    <svg
                      className="w-10 h-10 text-gray-50 absolute top-4 right-5 group-hover:text-yellow-50 transition-colors"
                      fill="currentColor"
                      viewBox="0 0 32 32"
                      aria-hidden="true"
                    >
                      <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                    </svg>
                  </div>

                  {/* Review Text */}
                  <p className="text-[14px] text-gray-600 italic leading-relaxed mb-5 flex-grow relative z-10">
                    &quot;{item.text}&quot;
                  </p>

                  {/* Tag */}
                  <div className="mb-6">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-none border border-yellow-200 bg-yellow-50 text-[11px] font-semibold text-yellow-700">
                      Đã mua: {item.product}
                    </span>
                  </div>

                  {/* Bottom: Avatar & Name */}
                  <div className="flex items-center gap-3 mt-auto pt-4 border-t border-gray-50">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-yellow-100 text-[#eab308] flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-[13px]">{item.name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{item.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-none border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
              Chưa có dữ liệu phản hồi.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
